/**
 * Sentry initialisation for the FaceWise frontend.
 *
 * Why this exists: a respondent's results can silently fail to arrive ("brak
 * danych") with no white screen and nothing in our logs — the failure happens
 * on someone else's device, mid-flow, and we never see it. Sentry gives us the
 * stack trace, the breadcrumb trail (what the user did just before), the
 * device/browser context, and — on errored sessions — a Session Replay so we
 * can watch exactly what the respondent saw.
 *
 * Privacy: the respondent flow handles camera/face/emotion data. Session Replay
 * is configured to mask ALL text and block ALL media, so no faces, no video and
 * no typed answers ever leave the browser — only a structural DOM reconstruction.
 *
 * Activation is DSN-gated: with no VITE_SENTRY_DSN set (e.g. local `vite` dev),
 * initSentry() is a no-op and the app behaves exactly as before. Set the DSN as
 * a CodeBuild project environment variable to turn it on for dev/prod builds.
 */
import * as Sentry from '@sentry/react'
import { ENV, ENV_NAME, resolveViteApiBaseUrl } from '@/config/environment'

/** The API origin, so browser-tracing can attach trace headers to our own API. */
function apiTracePropagationTargets(): (string | RegExp)[] {
  // Always allow same-origin requests. Add the API origin so that, once the
  // backend also runs Sentry, a single distributed trace links front → back.
  const targets: (string | RegExp)[] = [/^\//]
  try {
    const origin = new URL(resolveViteApiBaseUrl()).origin
    targets.push(origin)
  } catch {
    /* base URL not a valid absolute URL (local fallback) — same-origin only */
  }
  return targets
}

let initialised = false

export function initSentry(): void {
  const dsn = (import.meta.env.VITE_SENTRY_DSN as string | undefined)?.trim()
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.info('[Sentry] VITE_SENTRY_DSN not set — error monitoring disabled.')
    }
    return
  }
  if (initialised) return
  initialised = true

  Sentry.init({
    dsn,
    environment: ENV_NAME, // 'dev' | 'staging' | 'prod'
    release: `facewise-frontend@${ENV.VERSION}`,
    // Don't ship local-dev noise to the project unless explicitly building.
    enabled: true,

    integrations: [
      // Pageload + navigation transactions, and automatic fetch/xhr spans.
      Sentry.browserTracingIntegration(),
      // Session Replay — privacy-first: nothing visual or typed is captured.
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        maskAllInputs: true,
      }),
    ],

    // Performance: volume is tiny today, so trace everything. Turn down later.
    tracesSampleRate: 1.0,
    // Attach trace headers to our own API requests (cross-origin needs the
    // backend to allow `sentry-trace` + `baggage` in CORS — see note in PR).
    tracePropagationTargets: apiTracePropagationTargets(),

    // Replay: record 10% of healthy sessions, but 100% of any session that
    // hits an error — that's the respondent who got "brak danych".
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Drop the browser-extension / third-party noise that isn't our bug.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
    ],
  })
}

/**
 * Identify the current user on Sentry events. Called from AuthContext so every
 * issue is attributable to a specific researcher/respondent. Pass null on logout.
 *
 * The id, and not the address (Z-61). The backend runs Sentry with
 * send_default_pii=False and then the browser was sending the email anyway,
 * which made the backend setting decorative — the event arrived identified
 * either way. An id answers the only question this is for ("is this one
 * account or fifty?") and the address that goes with it is one query away for
 * whoever is entitled to make it.
 */
export function setSentryUser(
  user: { id: number | string; email?: string; is_researcher?: boolean } | null,
): void {
  if (!user) {
    Sentry.setUser(null)
    return
  }
  Sentry.setUser({
    id: String(user.id),
    role: user.is_researcher ? 'researcher' : 'respondent',
  })
}

export { Sentry }
